import React, { useState } from 'react';
import styled from 'styled-components';
import { Loading, MobileInput } from '../../../../components/elements';
import GoogleSuggest from '../../../../components/global/GoogleSuggest';
import PlaceSearchSelect from '../../../../components/global/PlaceSearchSelect';
import useCompanyForm from '../hooks/useCompanyForm';
import usePlaceLinking from '../hooks/usePlaceLinking';

// ─── Drawer Shell ─────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(22, 17, 13, 0.42);
  z-index: 400;
  backdrop-filter: blur(3px);
  display: ${({ open }) => (open ? 'block' : 'none')};
`;

const Drawer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 720px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(22, 17, 13, 0.13);
  z-index: 401;
  display: flex;
  flex-direction: column;
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerHeader = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid #E8DDD6;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DrawerTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #16110D;
`;

const DrawerSub = styled.div`
  font-size: 14px;
  color: #7A6A60;
  margin-top: 2px;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 22px;
`;

const DrawerFooter = styled.div`
  padding: 14px 22px;
  border-top: 1px solid #E8DDD6;
  flex-shrink: 0;
  display: flex;
  gap: 10px;
`;

// ─── Form Layout ──────────────────────────────────────────────────────────────

const FormSection = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #B8AAA2;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  margin: 18px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #E8DDD6;
  &:first-child {
    margin-top: 0;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
`;


const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7A6A60;
  margin-bottom: 5px;
`;

const inputBase = `
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #E8DDD6;
  border-radius: 8px;
  font-size: 15px;
  color: #16110D;
  background: #fff;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  transition: border-color 0.14s;
  &:focus {
    border-color: #F36E21;
  }
`;

const Input = styled.input`
  ${inputBase}
  border-color: ${({ hasError, abnState }) => {
    if (hasError) return '#C42B2B';
    if (abnState === 'valid') return '#1A7A4A';
    if (abnState === 'invalid') return '#C42B2B';
    return '#E8DDD6';
  }};
  &:disabled {
    background: #FAF8F6;
    color: #B8AAA2;
    cursor: not-allowed;
    border-color: #E8DDD6 !important;
  }
`;

const Select = styled.select`
  ${inputBase}
  cursor: pointer;
  &:focus {
    outline: 2px solid #F36E21;
    outline-offset: -1px;
    border-color: #E8DDD6;
  }
`;

const FieldError = styled.div`
  font-size: 13px;
  color: #C42B2B;
  margin-top: 4px;
`;

const ABNHint = styled.div`
  font-size: 13px;
  margin-top: 4px;
  color: ${({ state }) => {
    if (state === 'valid') return '#1A7A4A';
    if (state === 'invalid') return '#C42B2B';
    return '#B8AAA2';
  }};
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 15px;
  color: #3A2E26;
  margin-bottom: 10px;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

const SaveBtn = styled.button`
  flex: 2;
  cursor: pointer;
  border: none;
  outline: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  background: ${({ disabled }) => (disabled ? '#FAC9A3' : '#F36E21')};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 0.14s;
  &:hover:not(:disabled) {
    opacity: 0.88;
  }
`;

const CancelBtn = styled.button`
  flex: 1;
  cursor: pointer;
  border: 1px solid #E8DDD6;
  outline: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  background: #F2EDE8;
  color: #7A6A60;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.88;
  }
`;

const CloseBtn = styled.button`
  cursor: pointer;
  border: 1px solid #E8DDD6;
  outline: none;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  width: 30px;
  height: 30px;
  padding: 0;
  background: #F2EDE8;
  color: #7A6A60;
  flex-shrink: 0;
  &:hover { opacity: 0.8; }
`;

const GoogleAddressWrapper = styled.div`
  margin-bottom: 14px;

  .field {
    margin-bottom: 0;
  }

  label.label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #7A6A60;
    margin-bottom: 5px;
    display: block;
  }

  .control input.input {
    width: 100%;
    padding: 8px 12px !important;
    border: 1.5px solid #E8DDD6 !important;
    border-radius: 8px !important;
    font-size: 15px !important;
    color: #16110D !important;
    background: #fff !important;
    font-family: 'DM Sans', sans-serif !important;
    outline: none !important;
    box-shadow: none !important;
    height: auto !important;
    transition: border-color 0.14s;
    &:focus {
      border-color: #F36E21 !important;
      box-shadow: none !important;
    }
  }
`;

const OverrideRow = styled.div`
  margin-bottom: 12px;
  margin-top: -4px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

const FetchError = styled.div`
  padding: 20px;
  font-size: 15px;
  color: #C42B2B;
  text-align: center;
`;

const PlaceSearchWrapper = styled.div`
  .field {
    margin-bottom: 0;
  }
  label.label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #7A6A60;
    margin-bottom: 5px;
    display: block;
  }
  .control input.input {
    width: 100%;
    padding: 8px 12px !important;
    border: 1.5px solid #E8DDD6 !important;
    border-radius: 8px !important;
    font-size: 15px !important;
    color: #16110D !important;
    background: #fff !important;
    font-family: 'DM Sans', sans-serif !important;
    outline: none !important;
    box-shadow: none !important;
    height: auto !important;
    transition: border-color 0.14s;
    &:focus {
      border-color: #F36E21 !important;
      box-shadow: none !important;
    }
  }
`;

const PlaceLinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
`;

const PlaceLinkBtn = styled.button`
  flex-shrink: 0;
  cursor: pointer;
  border: none;
  outline: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  background: ${({ disabled }) => (disabled ? '#FAC9A3' : '#F36E21')};
  color: #fff;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  transition: opacity 0.14s;
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const PlaceList = styled.div`
  border: 1px solid #E8DDD6;
  border-radius: 8px;
  overflow: hidden;
`;

const PlaceListHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 64px;
  padding: 7px 12px;
  background: #FAF8F6;
  border-bottom: 1px solid #E8DDD6;
`;

const PlaceListItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 64px;
  align-items: center;
  padding: 9px 12px;
  border-bottom: 1px solid #FAF8F6;
  &:last-child { border-bottom: none; }
`;

const PlaceItemName = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: #16110D;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlaceItemAddr = styled.div`
  font-size: 13.5px;
  color: #7A6A60;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnlinkBtn = styled.button`
  cursor: pointer;
  border: 1px solid #FCEAEA;
  outline: none;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  background: #FCEAEA;
  color: #C42B2B;
  transition: opacity 0.14s;
  &:hover:not(:disabled) { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PlaceEmpty = styled.div`
  padding: 16px;
  font-size: 14px;
  color: #B8AAA2;
  text-align: center;
`;

// ─── Reusable field helpers ───────────────────────────────────────────────────

const FG = ({ label, error, children }) => (
  <FormGroup>
    <Label>{label}</Label>
    {children}
    {error && <FieldError>{error}</FieldError>}
  </FormGroup>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CompanyFormDrawer = ({ open, companyId, onClose, onSuccess }) => {
  const {
    fields,
    errors,
    fetchLoading,
    fetchError,
    submitLoading,
    abnStatus,
    isEdit,
    setField,
    handleABNChange,
    submit,
  } = useCompanyForm({ companyId, onSuccess });

  const [isManualAddress, setIsManualAddress] = useState(false);

  const setBankField = (key, value) =>
    setField('bank_detail', { ...fields.bank_detail, [key]: value });
  const setConfigField = (key, value) =>
    setField('kravein_configuration', { ...fields.kravein_configuration, [key]: value });

  const {
    selectedPlace,
    setSelectedPlace,
    linkedPlaces,
    loadingPlaces,
    linking,
    unlinkingId,
    resetKey,
    handleLinkPlace,
    handleUnlinkPlace,
  } = usePlaceLinking({ companyId });

  const handleAddressSelect = place => {
    setField('address_line_1', (place.addressLine1 || '').trim());
    setField('city', place.city || '');
    setField('post_code', place.postcode || '');
    setField('state', place.state || '');
    setField('country', place.country || 'AUSTRALIA');
  };

  const handleSubmit = async () => {
    await submit();
  };

  return (
    <>
      <Overlay open={open} onClick={onClose} />
      <Drawer open={open}>
        <DrawerHeader>
          <div>
            <DrawerTitle>{isEdit ? 'Edit Company' : 'New Company'}</DrawerTitle>
            <DrawerSub>All fields marked * are required</DrawerSub>
          </div>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          {fetchLoading ? (
            <LoadingWrap><Loading /></LoadingWrap>
          ) : fetchError ? (
            <FetchError>Failed to load company details. Please close and try again.</FetchError>
          ) : (
            <>
              {/* ── Company Details ── */}
              <FormSection>Company Details</FormSection>
              <TwoCol>
                <FG label="Registered Company Name *" error={errors.name}>
                  <Input
                    value={fields.name}
                    onChange={e => setField('name', e.target.value)}
                    placeholder="e.g. Hospitality Group Pty Ltd"
                    hasError={!!errors.name}
                  />
                </FG>
                <FG label="Status" error={errors.status}>
                  <Select
                    value={fields.status}
                    onChange={e => setField('status', e.target.value)}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </Select>
                </FG>
              </TwoCol>

              {/* ── Tax Details ── */}
              <FormSection>Tax Details</FormSection>
              <FG label="ABN (11 digits)" error={errors.registration_id}>
                <Input
                  value={fields.registration_id}
                  onChange={e => handleABNChange(e.target.value)}
                  placeholder="51 824 753 556"
                  maxLength={14}
                  hasError={!!errors.registration_id}
                  abnState={abnStatus}
                />
                {!errors.registration_id && abnStatus !== 'idle' && (
                  <ABNHint state={abnStatus}>
                    {abnStatus === 'valid' ? '✓ Valid ABN' : '✗ Invalid ABN — check and re-enter'}
                  </ABNHint>
                )}
              </FG>

              {/* ── Registered Address ── */}
              <FormSection>Registered Address</FormSection>
              <GoogleAddressWrapper>
                <GoogleSuggest
                  label="Search Address"
                  id="CompanyForm_SearchAddress"
                  initialValue={fields.city}
                  onSelect={handleAddressSelect}
                  disabled={isManualAddress}
                />
              </GoogleAddressWrapper>
              <OverrideRow>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={isManualAddress}
                    onChange={e => setIsManualAddress(e.target.checked)}
                    style={{ accentColor: '#F36E21' }}
                  />
                  Enter address manually
                </CheckLabel>
              </OverrideRow>
              <FG label="Street Address *" error={errors.address_line_1}>
                <Input
                  value={fields.address_line_1}
                  onChange={e => setField('address_line_1', e.target.value)}
                  placeholder="e.g. Level 2, 88 George St"
                  hasError={!!errors.address_line_1}
                  disabled={!isManualAddress}
                />
              </FG>
              <FG label="Address Line 2">
                <Input
                  value={fields.address_line_2}
                  onChange={e => setField('address_line_2', e.target.value)}
                  placeholder="Suite, floor, unit (optional)"
                  disabled={!isManualAddress}
                />
              </FG>
              <ThreeCol>
                <FG label="Suburb *" error={errors.city}>
                  <Input
                    value={fields.city}
                    onChange={e => setField('city', e.target.value)}
                    placeholder="Sydney"
                    hasError={!!errors.city}
                    disabled={!isManualAddress}
                  />
                </FG>
                <FG label="State *" error={errors.state}>
                  <Input
                    value={fields.state}
                    onChange={e => setField('state', e.target.value)}
                    placeholder="e.g. Victoria"
                    hasError={!!errors.state}
                    disabled={!isManualAddress}
                  />
                </FG>
                <FG label="Postcode *" error={errors.post_code}>
                  <Input
                    value={fields.post_code}
                    onChange={e => setField('post_code', e.target.value)}
                    placeholder="2000"
                    maxLength={4}
                    hasError={!!errors.post_code}
                    disabled={!isManualAddress}
                  />
                </FG>
              </ThreeCol>

              {/* ── Contact Details ── */}
              <FormSection>Contact Details</FormSection>
              <TwoCol>
                <FG label="Primary Contact Email" error={errors.primary_contact_email}>
                  <Input
                    type="email"
                    value={fields.primary_contact_email}
                    onChange={e => setField('primary_contact_email', e.target.value)}
                    placeholder="james@company.com.au"
                    hasError={!!errors.primary_contact_email}
                  />
                </FG>
                <FormGroup>
                  <MobileInput
                    label="Primary Contact Phone"
                    value={fields.primary_contact_phone}
                    onChange={value =>
                      setField(
                        'primary_contact_phone',
                        (value || '').replace(/\s+/g, ''),
                      )
                    }
                  />
                </FormGroup>
              </TwoCol>
              <FG label="Company Website">
                <Input
                  value={fields.business_site}
                  onChange={e => setField('business_site', e.target.value)}
                  placeholder="https://www.company.com.au"
                />
              </FG>

              {/* ── Bank / Settlement Details ── */}
              <FormSection>Bank / Settlement Details</FormSection>
              <TwoCol>
                <FG label="Account Name">
                  <Input
                    value={fields.bank_detail.account_name}
                    onChange={e => setBankField('account_name', e.target.value)}
                    placeholder="e.g. Hospitality Group Pty Ltd"
                  />
                </FG>
                <FG label="Account Number">
                  <Input
                    value={fields.bank_detail.account_number}
                    onChange={e => setBankField('account_number', e.target.value)}
                    placeholder="e.g. 1234567890"
                  />
                </FG>
              </TwoCol>
              <TwoCol>
                <FG label="Bank Name">
                  <Input
                    value={fields.bank_detail.bank_name}
                    onChange={e => setBankField('bank_name', e.target.value)}
                    placeholder="e.g. Commonwealth Bank"
                  />
                </FG>
                <FG label="Branch Name">
                  <Input
                    value={fields.bank_detail.branch_name}
                    onChange={e => setBankField('branch_name', e.target.value)}
                    placeholder="e.g. Sydney CBD"
                  />
                </FG>
              </TwoCol>
              <TwoCol>
                <FG label="IFSC Code">
                  <Input
                    value={fields.bank_detail.ifsc_code}
                    onChange={e => setBankField('ifsc_code', e.target.value)}
                    placeholder="e.g. HDFC0001234"
                  />
                </FG>
                <FG label="Payment Method">
                  <Select
                    value={fields.bank_detail.payment_method}
                    onChange={e => setBankField('payment_method', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Stripe Connect">Stripe Connect</option>
                    <option value="Manual EFT">Manual EFT</option>
                  </Select>
                </FG>
              </TwoCol>

              {/* ── KRAVEiN Invoice Configuration ── */}
              <FormSection>KRAVEiN Invoice Configuration</FormSection>
              <TwoCol>
                <FG label="Invoice Cycle">
                  <Select
                    value={fields.kravein_configuration.invoice_cycle}
                    onChange={e => setConfigField('invoice_cycle', e.target.value)}
                  >
                    <option value="Weekly">Weekly (Tue–Mon)</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Custom">Custom</option>
                  </Select>
                </FG>
                <FG label="Auto-send Invoice">
                  <Select
                    value={String(fields.kravein_configuration.auto_send_invoice)}
                    onChange={e => setConfigField('auto_send_invoice', e.target.value === 'true')}
                  >
                    <option value="true">Yes — auto-send on generation</option>
                    <option value="false">No — manual approval required</option>
                  </Select>
                </FG>
              </TwoCol>
              <FG label="Kravein Invoice Email">
                <Input
                  type="email"
                  value={fields.kravein_configuration.invoice_email}
                  onChange={e => setConfigField('invoice_email', e.target.value)}
                  placeholder="Uses finance contact email if blank"
                />
              </FG>

              {/* ── Linked Places ── */}
              {isEdit && (
                <>
                  <FormSection>Linked Places</FormSection>
                  <PlaceLinkRow>
                    <PlaceSearchWrapper style={{ flex: 1 }}>
                      <PlaceSearchSelect
                        key={resetKey}
                        onSelect={place => setSelectedPlace(place)}
                      />
                    </PlaceSearchWrapper>
                    <PlaceLinkBtn
                      type="button"
                      disabled={!selectedPlace || linking}
                      onClick={handleLinkPlace}
                    >
                      {linking ? 'Linking…' : '+ Link Place'}
                    </PlaceLinkBtn>
                  </PlaceLinkRow>
                  <PlaceList>
                    <PlaceListHeader>
                      <Label>Place</Label>
                      <Label>Address</Label>
                      <span />
                    </PlaceListHeader>
                    {loadingPlaces ? (
                      <PlaceEmpty>Loading…</PlaceEmpty>
                    ) : linkedPlaces.length === 0 ? (
                      <PlaceEmpty>No places linked to this company yet.</PlaceEmpty>
                    ) : (
                      linkedPlaces.map(place => (
                        <PlaceListItem key={place.place_id}>
                          <PlaceItemName title={place.name}>{place.name}</PlaceItemName>
                          <PlaceItemAddr>
                            {[place.address_line_1, place.city].filter(Boolean).join(', ')}
                          </PlaceItemAddr>
                          <UnlinkBtn
                            type="button"
                            disabled={!!unlinkingId}
                            onClick={() => handleUnlinkPlace(place)}
                          >
                            {unlinkingId === place.place_id ? '…' : 'Unlink'}
                          </UnlinkBtn>
                        </PlaceListItem>
                      ))
                    )}
                  </PlaceList>
                </>
              )}
            </>
          )}
        </DrawerBody>

        <DrawerFooter>
          <SaveBtn
            disabled={submitLoading || fetchLoading}
            onClick={handleSubmit}
          >
            {submitLoading ? 'Saving...' : '✓ Save Company'}
          </SaveBtn>
          <CancelBtn onClick={onClose}>Cancel</CancelBtn>
        </DrawerFooter>
      </Drawer>
    </>
  );
};

export default CompanyFormDrawer;
